
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import AlumniCard from "@/components/AlumniCard";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

// Type definition for alumni data
interface AlumniProfile {
  id: string;
  title?: string;
  company?: string;
  school?: string;
  years_experience?: number;
  expertise?: string;
  profiles: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
  services: {
    id: string;
    title: string;
    price: number;
  }[];
}

const Browse = () => {
  const [alumni, setAlumni] = useState<AlumniProfile[]>([]);
  const [filteredAlumni, setFilteredAlumni] = useState<AlumniProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        setLoading(true);
        
        // Fetch alumni with profiles and services
        const { data, error } = await supabase
          .from("alumni_profiles")
          .select(`
            id,
            school,
            degree,
            field_of_study,
            expertise,
            years_experience,
            hourly_rate,
            title,
            company,
            profiles:profiles!alumni_profiles_id_fkey (
              first_name, 
              last_name,
              avatar_url
            ),
            alumni_services (
              id,
              title,
              price
            )
          `);

        if (error) {
          throw error;
        }

        // Transform the data to match the AlumniProfile interface
       const formattedData = data.map((item: any) => ({
  id: item.id, // Ensure this comes directly from the database and is valid
  title: item.title || "",
  company: item.company || "",
  profiles: {
    first_name: item.profiles?.first_name || "",
    last_name: item.profiles?.last_name || "",
    avatar_url: item.profiles?.avatar_url || null,
  },
  services: item.alumni_services || [],
  expertise: item.expertise || "",
}));

        setAlumni(formattedData);
        setFilteredAlumni(formattedData);
      } catch (error: any) {
        console.error("Error fetching alumni:", error);
        toast({
          title: "Error",
          description: "Failed to load alumni profiles. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAlumni();
  }, [toast]);

  // Search alumni across all fields
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredAlumni(alumni);
      return;
    }
    
    const lowercaseQuery = query.toLowerCase();
    
    const filtered = alumni.filter(person => {
      // Search by name
      const fullName = `${person.profiles?.first_name || ''} ${person.profiles?.last_name || ''}`.toLowerCase();
      if (fullName.includes(lowercaseQuery)) return true;
      
      // Search by school
      if (person.school?.toLowerCase().includes(lowercaseQuery)) return true;
      
      // Search by company
      if (person.company?.toLowerCase().includes(lowercaseQuery)) return true;
      
      // Search by title
      if (person.title?.toLowerCase().includes(lowercaseQuery)) return true;
      
      // Search by expertise
      if (person.expertise?.toLowerCase().includes(lowercaseQuery)) return true;
      
      // Search by services
      if (person.services?.some(service => 
        service.title.toLowerCase().includes(lowercaseQuery)
      )) return true;
      
      return false;
    });
    
    setFilteredAlumni(filtered);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <NavBar />
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold font-playfair mb-2">Browse Alumni</h1>
        <p className="text-gray-600 mb-8">
          Connect with alumni from top institutions who can provide personalized guidance
        </p>

        <div className="mb-8 max-w-3xl mx-auto">
          <SearchBar 
            onSearch={handleSearch} 
            initialQuery={searchQuery} 
          />
        </div>

        <div className="w-full">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 rounded-lg bg-gray-100 animate-pulse"></div>
              ))}
            </div>
          ) : filteredAlumni.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredAlumni.map((alumni) => (
                <AlumniCard key={alumni.id} alumni={alumni} showPricing={true} />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <h3 className="text-lg font-medium mb-2">No alumni found</h3>
              <p className="text-gray-500 mb-4">
                Try adjusting your search criteria
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery("");
                  handleSearch("");
                }}
              >
                Reset Search
              </Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Browse;
